import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/use-toast';
import { EyeOff, CheckCircle, Users } from 'lucide-react';
import { AccountFinal } from '@/api/entities';
import { PersonFinal } from '@/api/entities';

export default function HideHongKongAccounts() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const { toast } = useToast();

  const accountsToHide = [
    'City University of Hong Kong (CityU)',
    'The Hong Kong University of Science and Technology (HKUST)'
  ];

  const handleHideAccounts = async () => {
    setIsProcessing(true);
    const processingResults = [];

    try {
      const allAccounts = await AccountFinal.list();
      const allPersonas = await PersonFinal.list();

      for (const accountName of accountsToHide) {
        try {
          const account = allAccounts.find(a => a.account_name === accountName);

          if (!account) {
            processingResults.push({
              accountName,
              status: 'not_found',
              message: 'Account not found',
              personasHidden: 0
            });
            continue;
          }

          // Update account tenant_overrides to hide for instructure
          const existingOverrides = account.tenant_overrides || {};
          const updatedOverrides = {
            ...existingOverrides,
            instructure: {
              ...(existingOverrides.instructure || {}),
              is_visible: false
            }
          };

          await AccountFinal.update(account.id, { tenant_overrides: updatedOverrides });

          // Find all personas linked to this account
          const linkedPersonas = allPersonas.filter((p) =>
            p.account_name === accountName ||
            p.account_id === account.id ||
            p.account_id === account.account_id ||
            (account.global_pool_account_id && p.account_id === account.global_pool_account_id)
          );

          console.log(`Found ${linkedPersonas.length} personas for ${accountName}`);

          // Hide each persona for instructure tenant
          let personasHidden = 0;
          for (const persona of linkedPersonas) {
            try {
              const personaOverrides = persona.tenant_overrides || {};
              const updatedPersonaOverrides = {
                ...personaOverrides,
                instructure: {
                  ...(personaOverrides.instructure || {}),
                  is_visible: false
                }
              };

              await PersonFinal.update(persona.id, { tenant_overrides: updatedPersonaOverrides });
              personasHidden++;

              // Add delay to avoid rate limiting
              if (personasHidden < linkedPersonas.length) {
                await new Promise(resolve => setTimeout(resolve, 700));
              }
            } catch (error) {
              console.error(`Error hiding persona ${persona.id}:`, error);
            }
          }

          processingResults.push({
            accountName,
            status: 'success',
            message: `Hidden for instructure tenant`,
            personasHidden
          });
        } catch (error) {
          processingResults.push({
            accountName,
            status: 'error',
            message: error.message,
            personasHidden: 0
          });
        }
      }

      setResults(processingResults);
      
      const successCount = processingResults.filter(r => r.status === 'success').length;
      const totalPersonasHidden = processingResults.reduce((sum, r) => sum + (r.personasHidden || 0), 0);
      
      toast({
        title: 'Processing Complete',
        description: `${successCount} accounts and ${totalPersonasHidden} personas hidden for instructure`
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <EyeOff className="w-5 h-5" />
          Hide Hong Kong Accounts & Personas for Instructure
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-gray-600">
          <p className="mb-2">This will hide the following accounts <strong>and all their linked personas</strong> for the <strong>instructure</strong> tenant only:</p>
          <ul className="list-disc list-inside space-y-1">
            {accountsToHide.map(name => (
              <li key={name}>{name}</li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-gray-500">
            Note: These accounts and personas will remain visible to other tenants (clairelabs, smowl, etc.)
          </p>
        </div>

        <Button
          onClick={handleHideAccounts}
          disabled={isProcessing}
          className="w-full"
        >
          {isProcessing ? 'Processing...' : 'Hide Accounts & Personas for Instructure'}
        </Button>

        {results && (
          <div className="mt-4 space-y-2">
            <h4 className="font-semibold text-sm">Results:</h4>
            {results.map((result, idx) => (
              <div
                key={idx}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  result.status === 'success'
                    ? 'bg-green-50 border-green-200'
                    : result.status === 'not_found'
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    {result.status === 'success' && <CheckCircle className="w-4 h-4 text-green-600" />}
                    <span className="text-sm font-medium">{result.accountName}</span>
                  </div>
                  {result.personasHidden > 0 && (
                    <div className="flex items-center gap-1 ml-6 text-xs text-gray-600">
                      <Users className="w-3 h-3" />
                      <span>{result.personasHidden} persona(s) hidden</span>
                    </div>
                  )}
                </div>
                <span className="text-xs text-gray-600">{result.message}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}